import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUserCog } from '@fortawesome/free-solid-svg-icons';
import type { User } from '../types';

const DARK_COLOR = '#444444';
const ACCENT_COLOR = '#fec35c';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onUpdateDisplayName?: (name: string) => Promise<boolean>;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  user,
  onUpdateDisplayName,
}) => {
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  if (!isOpen) return null;

  const handleSaveDisplayName = async () => {
    if (!onUpdateDisplayName) {
      setIsEditingName(false);
      return;
    }

    setIsSaving(true);
    try {
      const success = await onUpdateDisplayName(displayName);
      if (success) {
        setIsEditingName(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown';

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5000,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '4px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(229, 231, 235, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: ACCENT_COLOR,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon
                icon={faUserCog}
                style={{ color: 'white', width: 24, height: 24 }}
              />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: DARK_COLOR,
                  margin: 0,
                  fontFamily: 'Lato, system-ui, sans-serif',
                }}
              >
                Account Settings
              </h2>
              <p
                style={{
                  fontSize: '0.75rem',
                  color: '#666',
                  margin: 0,
                  fontFamily: 'Lato, system-ui, sans-serif',
                }}
              >
                Manage your account
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Close"
          >
            <FontAwesomeIcon
              icon={faTimes}
              style={{ color: DARK_COLOR, width: 16, height: 16 }}
            />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '2rem',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Profile Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#666',
                marginBottom: '1rem',
                fontFamily: 'Lato, system-ui, sans-serif',
              }}
            >
              Profile
            </h3>
            <div
              style={{
                backgroundColor: '#f9fafb',
                borderRadius: '4px',
                padding: '1rem',
              }}
            >
              {/* Display Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#666',
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontFamily: 'Lato, system-ui, sans-serif',
                  }}
                >
                  Display Name
                </label>
                {isEditingName ? (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem',
                        fontFamily: 'Lato, system-ui, sans-serif',
                        outline: 'none',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = ACCENT_COLOR;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                      }}
                    />
                    <button
                      onClick={handleSaveDisplayName}
                      disabled={isSaving}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: ACCENT_COLOR,
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        opacity: isSaving ? 0.7 : 1,
                        fontFamily: 'Lato, system-ui, sans-serif',
                      }}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setDisplayName(user?.display_name || '');
                        setIsEditingName(false);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e5e7eb',
                        color: DARK_COLOR,
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontFamily: 'Lato, system-ui, sans-serif',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span
                      style={{
                        fontSize: '0.875rem',
                        color: DARK_COLOR,
                        fontFamily: 'Lato, system-ui, sans-serif',
                      }}
                    >
                      {user?.display_name || 'Not set'}
                    </span>
                    <button
                      onClick={() => setIsEditingName(true)}
                      style={{
                        fontSize: '0.75rem',
                        color: ACCENT_COLOR,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'Lato, system-ui, sans-serif',
                      }}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#666',
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontFamily: 'Lato, system-ui, sans-serif',
                  }}
                >
                  Email
                </label>
                <span
                  style={{
                    fontSize: '0.875rem',
                    color: DARK_COLOR,
                    fontFamily: 'Lato, system-ui, sans-serif',
                  }}
                >
                  {user?.email || 'Not available'}
                </span>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#666',
                marginBottom: '1rem',
                fontFamily: 'Lato, system-ui, sans-serif',
              }}
            >
              Security
            </h3>
            <div
              style={{
                backgroundColor: '#f9fafb',
                borderRadius: '4px',
                padding: '1rem',
              }}
            >
              {showPasswordSection ? (
                <div>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: '#666',
                      marginBottom: '1rem',
                      fontFamily: 'Lato, system-ui, sans-serif',
                    }}
                  >
                    Password change functionality coming soon.
                  </p>
                  <button
                    onClick={() => setShowPasswordSection(false)}
                    style={{
                      fontSize: '0.75rem',
                      color: ACCENT_COLOR,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'Lato, system-ui, sans-serif',
                    }}
                  >
                    Hide
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowPasswordSection(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: DARK_COLOR,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'Lato, system-ui, sans-serif',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = ACCENT_COLOR;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = DARK_COLOR;
                  }}
                >
                  Change Password
                </button>
              )}
            </div>
          </div>

          {/* Account Section */}
          <div>
            <h3
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#666',
                marginBottom: '1rem',
                fontFamily: 'Lato, system-ui, sans-serif',
              }}
            >
              Account
            </h3>
            <div
              style={{
                backgroundColor: '#f9fafb',
                borderRadius: '4px',
                padding: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span
                  style={{
                    fontSize: '0.75rem',
                    color: '#666',
                    fontFamily: 'Lato, system-ui, sans-serif',
                  }}
                >
                  Member since:
                </span>
                <span
                  style={{
                    fontSize: '0.875rem',
                    color: DARK_COLOR,
                    fontFamily: 'Lato, system-ui, sans-serif',
                  }}
                >
                  {memberSince}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 2rem',
            borderTop: '1px solid rgba(229, 231, 235, 0.5)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: ACCENT_COLOR,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontFamily: 'Lato, system-ui, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f16952';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = ACCENT_COLOR;
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SettingsModal;
